import SectionIcon from '@components/SectionIcon';
import { Flex, Select, Text } from '@retracedgmbh/ui-components-lib';
import useTranslator from '@/hooks/useTranslator';
import type { FileClassificationType } from '@/services/api/types/ai-processing';
import { FILE_CLASSIFICATION_OPTIONS } from '@/services/api/types/ai-processing';

interface UploadedFileCardProps {
  fileName: string;
  fileSize?: number;
  status: 'processing' | 'pending' | 'completed' | 'failed';
  classificationType: FileClassificationType | null;
  onTypeChange: (type: FileClassificationType) => void;
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function UploadedFileCard({
  fileName,
  fileSize,
  status,
  classificationType,
  onTypeChange,
}: UploadedFileCardProps) {
  const { getTranslation } = useTranslator({ page: 'ai-processing' });
  const isClassifying = status === 'processing' || status === 'pending';
  const selectedOption = classificationType
    ? (FILE_CLASSIFICATION_OPTIONS.find(o => o.key === classificationType) ??
      null)
    : null;

  return (
    <Flex
      padding='lg'
      gap='md'
      border='sm'
      borderColor='primary'
      borderRadius='lg'
    >
      {/* File icon */}
      <SectionIcon icon='fa-regular fa-file' type='primary' />

      {/* File info */}
      <Flex direction='column' gap='xxs' className='tw:flex-1 tw:min-w-0'>
        <Text size='base' weight='semibold' textOverFlow='truncate'>
          {fileName}
        </Text>
        <Flex gap='sm' alignItems='center'>
          {fileSize ? (
            <Text size='small' color='secondary'>
              {formatFileSize(fileSize)}
            </Text>
          ) : null}
        </Flex>
      </Flex>

      {/* Right side: classifying spinner or type select */}
      {isClassifying ? (
        <Flex
          alignItems='center'
          gap='sm'
          className='tw:shrink-0'
          marginRight='lg'
        >
          <i className='fa-light fa-circle-notch tw:animate-spin tw:text-content-brand' />
          <Text size='small' color='brand'>
            {getTranslation('upload.classifying')}
          </Text>
        </Flex>
      ) : (
        <Flex className='tw:shrink-0'>
          <Select
            options={FILE_CLASSIFICATION_OPTIONS}
            value={selectedOption}
            onChange={(opt: { key: string }) =>
              onTypeChange(opt.key as FileClassificationType)
            }
            width={150}
          />
        </Flex>
      )}
    </Flex>
  );
}
